import { useEffect, useRef, useState } from "react";

import {
  apiListNotifications,
  apiMarkNotificationRead,
  apiRespondCollabJoin,
  type NotificationItem,
} from "../data/api";
import type { CollabAPI } from "../collab/Collab";

import "./NotificationCenter.scss";

const POLL_MS = 2_500;

type Props = {
  collabAPI: CollabAPI | null;
  drawingId: string | null;
};

// Popup que escuta pedidos de colaboração entrantes (collab_join_request) ENQUANTO
// a Alice já está no editor. Sem isso, ela teria que voltar pro dashboard e abrir
// o sininho — perde o contexto do drawing aberto.
//
// Quando um pedido chega para o drawing atualmente aberto, mostra um modal.
// Aceitar: inicia a sala localmente (collabAPI.startCollaboration) e manda o
// roomUrl pro pedinte. Recusar: apenas marca como lida e responde decline.
export const CollabRequestPopup = ({ collabAPI, drawingId }: Props) => {
  const [pending, setPending] = useState<NotificationItem | null>(null);
  const [working, setWorking] = useState<"accept" | "decline" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dismissedIds = useRef<Set<string>>(new Set());

  // Polling por pedidos novos enquanto este componente está montado (no editor).
  useEffect(() => {
    if (!drawingId) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled || pending) return;
      try {
        const list = await apiListNotifications();
        const match = list.find(
          (n) =>
            n.type === "collab_join_request" &&
            n.payload.drawingId === drawingId &&
            !n.read_at &&
            !dismissedIds.current.has(n.id),
        );
        if (match && !cancelled) {
          setPending(match);
        }
      } catch {
        /* silencioso */
      }
    };
    tick();
    const handle = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [drawingId, pending]);

  if (!pending) return null;

  const { payload } = pending;

  const handleAccept = async () => {
    if (!collabAPI) {
      setError("Colaboração indisponível neste contexto.");
      return;
    }
    setWorking("accept");
    setError(null);
    try {
      await apiMarkNotificationRead(pending.id).catch(() => {});

      if (!collabAPI.isCollaborating()) {
        await collabAPI.startCollaboration(null);
        // Pequena pausa pra window.history.pushState propagar.
        await new Promise((r) => window.setTimeout(r, 300));
      }
      const roomUrl = window.location.href;
      if (!/#room=/.test(roomUrl)) {
        throw new Error("Não foi possível obter o link da sala.");
      }
      await apiRespondCollabJoin(payload.drawingId, {
        recipientUserId: payload.fromUserId,
        roomUrl,
      });
      dismissedIds.current.add(pending.id);
      setPending(null);
    } catch (err: any) {
      setError(err?.message ?? "Falha ao aceitar.");
      setWorking(null);
    }
  };

  const handleDecline = async () => {
    setWorking("decline");
    setError(null);
    try {
      await apiMarkNotificationRead(pending.id).catch(() => {});
      await apiRespondCollabJoin(payload.drawingId, {
        recipientUserId: payload.fromUserId,
        decline: true,
      });
      dismissedIds.current.add(pending.id);
      setPending(null);
    } catch (err: any) {
      setError(err?.message ?? "Falha ao recusar.");
      setWorking(null);
    }
  };

  const handleDismiss = () => {
    // Só fecha o modal localmente; a notif fica não-lida pro user decidir depois.
    dismissedIds.current.add(pending.id);
    setPending(null);
  };

  return (
    <div className="NotificationCenter__modalBackdrop">
      <div className="NotificationCenter__modal">
        <button
          type="button"
          className="NotificationCenter__modalClose"
          onClick={handleDismiss}
          aria-label="Fechar"
        >
          ×
        </button>
        <h3 className="NotificationCenter__modalTitle">
          {payload.fromName} quer entrar na sala
        </h3>
        <div className="NotificationCenter__modalDrawing">
          <strong>{payload.drawingName}</strong>
          <span>
            {payload.fromName} pediu para entrar na sala de colaboração deste
            desenho. Ao aceitar, a sala ao vivo será aberta com o conteúdo atual.
          </span>
        </div>
        <div className="NotificationCenter__modalActions">
          <button
            type="button"
            className="NotificationCenter__actionBtn NotificationCenter__actionBtn--primary"
            onClick={handleAccept}
            disabled={working !== null}
          >
            {working === "accept" ? "Abrindo sala..." : "Aceitar e abrir a sala"}
            <span>Inicia colaboração ao vivo neste desenho</span>
          </button>
          <button
            type="button"
            className="NotificationCenter__actionBtn"
            onClick={handleDecline}
            disabled={working !== null}
          >
            {working === "decline" ? "Recusando..." : "Recusar"}
            <span>{payload.fromName} será notificado(a) da recusa</span>
          </button>
        </div>
        {error && <div className="NotificationCenter__modalError">{error}</div>}
      </div>
    </div>
  );
};
